import { resolveMoneyDecimals, subtractMoney } from "../utils/money";
import { formatNumberByCountry } from "../utils/formatNumbers";

/**
 * Si Business Central tiene cobrado lo que se le va a pedir que registre.
 *
 * Existe porque un pago reenviado podía perderse en silencio: la acción de pagos
 * respondía `Ok` sobre una línea que ya existía **sin cambiar el importe**.
 * Medido en Costa Rica DEV el 12/09/2026, sobre `VF15-009085`: la línea 20001 por
 * 7.999 y luego por 8.000, las dos veces `Ok`, y quedó en 7.999. El registro
 * posterior se rechazaba con `PAGO_INCOMPLETO`, y el terminal dejaba el
 * documento fuera de la sincronización para siempre.
 *
 * Así que antes de registrar se pregunta, con la misma consulta que ya existía
 * (`estadoFactura`), y se compara en **las dos direcciones**:
 *
 *     | |importeLineas| − |importePagos| | > toleranciaPagos, sólo en borrador
 *
 * El valor absoluto de fuera es del 20/09/2026 y es lo único que separa esto de
 * lo que había: lo cobrado tiene que ser **igual** al importe del documento, no
 * «al menos» el importe del documento.
 *
 * La tolerancia **la da Business Central** y no se calcula aquí: sale de la
 * precisión de redondeo de la divisa, que el terminal no conoce, y un 0,01 fijo
 * bloquearía en guaraníes lo que la guarda acepta.
 *
 * Ante cualquier duda no se bloquea --respuesta nula, documento que no está en
 * borrador, o una versión de Business Central que todavía no manda
 * `toleranciaPagos`--: se registra como siempre, y la guarda de Business Central
 * sigue siendo la que decide. Acuerdo
 * `contrato-hallazgos-ciclo-cri-2026-09-12.md`, H1.
 */

/** Un número de la respuesta, o `null` si no viene o no es un número. */
const numeroDe = (valor) => {
  if (valor === null || valor === undefined || String(valor).trim() === "") return null;
  const numero = Number(valor);
  return Number.isFinite(numero) ? numero : null;
};

/**
 * Si lo cobrado en Business Central no es igual al importe del documento.
 *
 * **Mira las dos direcciones, y antes sólo miraba una.** Hasta el 20/09/2026
 * esto se llamaba `cobroQueFaltaEnBC` y restaba `total − pagos`: si se pagaba de
 * más, la resta salía negativa, nunca superaba la tolerancia y devolvía `null`,
 * que significa «todo bien, seguid». La guarda de Business Central tiene el
 * mismo punto ciego --su `if decTotal - decPagado > decTolerancia` y esta resta
 * son la misma decisión tomada dos veces-- así que el sobrepago pasaba por los
 * dos lados sin una queja.
 *
 * Medido en PAN DEV el 20/09/2026: un pago de 21,40 contra un documento de
 * 20,00 se registró sin un aviso. Y el daño no es sólo el arqueo del cajón: el
 * apunte del cobro va por 21,40 contra el cliente y la factura por 20,00, así
 * que **al cliente le queda 1,40 a favor que nadie pidió**. El descuadre de la
 * caja y el saldo del cliente son la misma moneda por sus dos caras.
 *
 * Los dos nombres --`falta` aquí, `PAGO_INCOMPLETO` allá-- nombran una sola
 * dirección, y la resta salió en el sentido del nombre sin que nadie lo
 * decidiera. Por eso ahora se llama «no cuadra», que no tiene sentido.
 *
 * No choca con el ajuste de la última forma de pago que corre antes: aquél
 * iguala los importes cuando la diferencia es de redondeo (hasta 0,05) y se
 * declara incompetente por encima. Esta guarda muerde exactamente donde aquél
 * dijo «esto no es redondeo, no lo tapo».
 *
 * @param {object|null} estado Lo que devolvió `consultarEstadoFactura`.
 * @param {object} country El país activo, para la precisión de cálculo.
 * @returns {{total: number, pagos: number, diferencia: number, sobra: boolean}|null}
 */
export const cobroQueNoCuadraEnBC = (estado, country) => {
  if (!estado || typeof estado !== "object") return null;
  if (estado.estado !== "borrador") return null;

  const tolerancia = numeroDe(estado.toleranciaPagos);
  const total = numeroDe(estado.importeLineas);
  const pagos = numeroDe(estado.importePagos);
  if (tolerancia === null || total === null || pagos === null) return null;

  // Positiva = falta por cobrar. Negativa = se cobró de más.
  const diferencia = subtractMoney(
    Math.abs(total),
    Math.abs(pagos),
    resolveMoneyDecimals(country)
  );
  if (Math.abs(diferencia) <= tolerancia) return null;

  return {
    total: Math.abs(total),
    pagos: Math.abs(pagos),
    diferencia,
    sobra: diferencia < 0,
  };
};

/**
 * El aviso para el cajero, con los dos importes que Business Central tiene.
 *
 * Los dos casos se arreglan de forma distinta y por eso no comparten texto: lo
 * que falta se manda de nuevo solo, y lo que sobra **no se puede retirar desde
 * el terminal** --los pagos no se retiran, acuerdo del 12/09-- así que hay que
 * pedirlo allá. Un texto único mandaría al cajero a esperar algo que no va a
 * pasar, o a pedir algo que no hace falta.
 *
 * @param {{total: number, pagos: number, sobra: boolean}} cobro
 * @param {object} country
 */
export const avisoDelCobroQueNoCuadra = ({ total, pagos, sobra }, country) => {
  const importe = (valor) => formatNumberByCountry(valor, country) || String(valor);
  const inicio = `Business Central tiene pagos por ${importe(pagos)} y un total de ${importe(
    total
  )}.`;

  return sobra
    ? `${inicio} Se cobró de más: pide que retiren el pago sobrante en Business Central. ` +
        "Si se registra así, al cliente le queda esa diferencia a favor."
    : inicio;
};

/**
 * Cuántos pagos tiene Business Central y cuántos manda este terminal, si no
 * coinciden; si coinciden, `null`.
 *
 * `cobroQueFaltaEnBC` sólo mira el infrapago, y hay un sobrepago que no se ve:
 * una venta que quedó fuera de la cola --por `DATOS_INVALIDOS` o `SIN_CORREO`--
 * vuelve a ser editable, el cajero quita una forma de pago y se borra en local,
 * pero Business Central conserva la suya, porque los pagos no se retiran
 * (acuerdo del 12/09). Registrar ahí timbra un cobro que el terminal no tiene.
 *
 * En borrador `numPagos` cuenta todas las filas de `Detalle Pago Factura` del
 * documento, así que la comparación es exacta. Sólo cuentan los pagos que
 * viajan: las líneas deducidas no llegan a Business Central.
 *
 * Ante cualquier duda no se bloquea: respuesta nula, documento que no está en
 * borrador, o una BC que no manda `numPagos`. Acuerdo
 * `contrato-hallazgos-ciclo-cri-2026-09-14.md`, H1 · TPV.
 *
 * @param {object|null} estado Lo que devolvió `consultarEstadoFactura`.
 * @param {Array} pagosLocales Las líneas de pago locales; las deducidas se descartan acá.
 * @returns {{enBC: number, enTerminal: number}|null}
 */
export const pagosQueNoCuadranEnBC = (estado, pagosLocales) => {
  if (!estado || typeof estado !== "object") return null;
  if (estado.estado !== "borrador") return null;

  const enBC = numeroDe(estado.numPagos);
  if (enBC === null) return null;

  const enTerminal = (pagosLocales || []).filter((pago) => !pago?.deducida).length;
  return enBC === enTerminal ? null : { enBC, enTerminal };
};

/**
 * El motivo para la fila cuando no coincide el número de pagos.
 *
 * El contrato describe el caso de Business Central con **más** pagos, que es el
 * que el terminal no puede arreglar: hay que retirarlo allá. El de **menos** se
 * arregla reenviando los pagos, y decirle al cajero que pida retirar algo sería
 * mandarlo a buscar un pago que no existe.
 *
 * @param {{enBC: number, enTerminal: number}} pagos
 */
export const avisoDePagosQueNoCuadran = ({ enBC, enTerminal }) => {
  const inicio = `La venta no se registró: Business Central tiene ${enBC} pago(s) y este terminal ${enTerminal}.`;
  return enBC > enTerminal
    ? `${inicio} Un pago que se quitó en el terminal sigue en Business Central; pide que lo retiren allá.`
    : `${inicio} Se volverán a enviar los pagos en la siguiente sincronización.`;
};

/** Si el registro se rechazó porque los pagos no cubren el documento. */
export const esRechazoPorPagoIncompleto = (mensaje) => /PAGO_INCOMPLETO/.test(String(mensaje ?? ""));
